import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { getBankDetails, updateBank, clearError, clearSuccess, clearBankDetails } from '../../../../redux/Slices/authSlice';
import { FaArrowRight } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import i18n from '../../../../i18n/i18n';
import { useTitle } from '../../../../shared/hooks/TitleContext';
import { applyFieldFilter } from '../../../../shared/utils.js/fieldValidation';

const EditBank = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setTitle } = useTitle();
  const { id } = useParams();
  const { bankDetails, isLoading, error, success } = useSelector((state) => state.auth);
  const lastErrorRef = useRef({ message: null, time: 0 });
  const lastSuccessRef = useRef({ message: null, time: 0 });
  const { t } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const textAlign = isRTL ? 'right' : 'left';
  const dir = isRTL ? 'rtl' : 'ltr';

  const [formData, setFormData] = useState({
    name_en: '',
    name_ar: '',
    account_number: '',
    iban: '',
    swift_code: '',
    is_active: true,
    notes: ''
  });

  useEffect(() => {
    setTitle('banks.edit.title');
  }, [setTitle]);

  useEffect(() => {
    if (id) {
      dispatch(getBankDetails(id));
    }
    return () => {
      dispatch(clearBankDetails());
    };
  }, [dispatch, id]);

  useEffect(() => {
    if (bankDetails) {
      const details = bankDetails.data || bankDetails;
      setFormData({
        name_en: details.name_en || details.name || '',
        name_ar: details.name_ar || details.name || '',
        account_number: details.account_number || '',
        iban: details.iban || '',
        swift_code: details.swift_code || '',
        is_active: details.is_active !== undefined ? details.is_active : true,
        notes: details.notes || ''
      });
    }
  }, [bankDetails]);

  useEffect(() => {
    if (success) {
      const now = Date.now();
      const last = lastSuccessRef.current;
      if (!last.message || last.message !== success || now - last.time > 2000) {
        toast.success(success, { 
          rtl: isRTL,
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
        lastSuccessRef.current = { message: success, time: now };
      }
      setTimeout(() => {
        dispatch(clearSuccess());
        navigate('/banks');
      }, 1500);
    }
  }, [success, dispatch, navigate, isRTL]);

  useEffect(() => {
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== error || now - last.time > 2000) {
        toast.error(error, { 
          rtl: isRTL,
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
        lastErrorRef.current = { message: error, time: now };
      }
      setTimeout(() => dispatch(clearError()), 3000);
    }
  }, [error, dispatch, isRTL]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const finalValue = type === 'checkbox' ? checked : applyFieldFilter(name, value);
    setFormData(prev => ({ ...prev, [name]: finalValue }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const submitData = {
      ...formData,
      is_active: formData.is_active ? true : false
    };
    dispatch(updateBank({ id, data: submitData }));
  };

  if (isLoading && !bankDetails) {
    return (
      <div style={{ 
        padding: '30px', 
        backgroundColor: '#14263b', 
        minHeight: 'calc(100vh - 80px)', 
        color: 'white',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        direction: dir
      }}>
        {t('common.loading', { defaultValue: 'Loading...' })}
      </div>
    );
  }

  return (
    <div style={{ 
      padding: '30px',
      backgroundColor: '#14263b',
      minHeight: 'calc(100vh - 80px)',
      color: 'white',
      direction: dir
    }}>
      <div style={{ marginBottom: '30px' }}>
        <button
          onClick={() => navigate('/banks')}
          style={{
            backgroundColor: '#666',
            color: 'white',
            border: 'none',
            padding: '10px',
            borderRadius: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '20px',
            fontSize: '14px',
            flexDirection: isRTL ? 'row' : 'row-reverse'
          }}
        >
          <FaArrowRight style={{ transform: isRTL ? 'rotate(180deg)' : 'none' }} />
          {t('common.back', { defaultValue: 'Back' })}
        </button>
        <h1 style={{ 
          fontSize: '24px', 
          fontWeight: 'bold', 
          margin: 0,
          textAlign
        }}>
          {t('banks.edit.title', { defaultValue: 'Edit Bank' })}
        </h1>
      </div>
      
      <form onSubmit={handleSubmit} style={{ 
        backgroundColor: '#1b3350',
        border: '1px solid #333',
        borderRadius: '12px',
        padding: '32px',
        width: '100%',
        maxWidth: '900px',
        direction: dir
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontSize: '14px', 
              fontWeight: 'bold', 
              color: 'white',
              textAlign,
              direction: dir
            }}>
              {t('banks.form.nameEn', { defaultValue: 'Name (English)' })} <span style={{ color: '#dc3545' }}>*</span>
            </label>
            <input
              type="text"
              name="name_en"
              value={formData.name_en}
              onChange={handleChange}
              required
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#1a1f2e',
                border: '1px solid #333',
                borderRadius: '8px',
                color: 'white',
                fontSize: '14px',
                boxSizing: 'border-box',
                direction: 'ltr',
                textAlign: 'left'
              }}
            />
          </div>

          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontSize: '14px', 
              fontWeight: 'bold', 
              color: 'white',
              textAlign,
              direction: dir
            }}>
              {t('banks.form.nameAr', { defaultValue: 'Name (Arabic)' })} <span style={{ color: '#dc3545' }}>*</span>
            </label>
            <input
              type="text"
              name="name_ar"
              value={formData.name_ar}
              onChange={handleChange}
              required
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#1a1f2e',
                border: '1px solid #333',
                borderRadius: '8px',
                color: 'white',
                fontSize: '14px',
                boxSizing: 'border-box',
                direction: 'rtl',
                textAlign: 'right'
              }}
            />
          </div>

          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontSize: '14px', 
              fontWeight: 'bold', 
              color: 'white',
              textAlign,
              direction: dir
            }}>
              {t('banks.form.accountNumber', { defaultValue: 'Account Number' })}
            </label>
            <input
              type="text"
              name="account_number"
              value={formData.account_number}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#1a1f2e',
                border: '1px solid #333',
                borderRadius: '8px',
                color: 'white',
                fontSize: '14px',
                boxSizing: 'border-box',
                direction: dir,
                textAlign
              }}
            />
          </div>

          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontSize: '14px', 
              fontWeight: 'bold', 
              color: 'white',
              textAlign,
              direction: dir
            }}>
              {t('banks.form.iban', { defaultValue: 'IBAN' })}
            </label>
            <input
              type="text"
              name="iban"
              value={formData.iban}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#1a1f2e',
                border: '1px solid #333',
                borderRadius: '8px',
                color: 'white',
                fontSize: '14px',
                boxSizing: 'border-box',
                direction: dir,
                textAlign
              }}
            />
          </div>

          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontSize: '14px', 
              fontWeight: 'bold', 
              color: 'white',
              textAlign,
              direction: dir
            }}>
              {t('banks.form.swiftCode', { defaultValue: 'SWIFT Code' })}
            </label>
            <input
              type="text"
              name="swift_code"
              value={formData.swift_code}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#1a1f2e',
                border: '1px solid #333',
                borderRadius: '8px',
                color: 'white',
                fontSize: '14px',
                boxSizing: 'border-box',
                direction: dir,
                textAlign
              }}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '30px', flexDirection: isRTL ? 'row' : 'row-reverse' }}>
            <input
              type="checkbox"
              name="is_active"
              checked={formData.is_active}
              onChange={handleChange}
              style={{
                width: '20px',
                height: '20px',
                cursor: 'pointer'
              }}
            />
            <label style={{ 
              fontSize: '14px', 
              fontWeight: 'bold', 
              color: 'white', 
              cursor: 'pointer',
              textAlign,
              direction: dir
            }}>
              {t('banks.form.isActive', { defaultValue: 'Active' })}
            </label>
          </div>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ 
            display: 'block', 
            marginBottom: '8px', 
            fontSize: '14px', 
            fontWeight: 'bold', 
            color: 'white',
            textAlign,
            direction: dir
          }}>
            {t('banks.form.notes', { defaultValue: 'Notes' })}
          </label>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows="4"
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: '#1a1f2e',
              border: '1px solid #333',
              borderRadius: '8px',
              color: 'white',
              fontSize: '14px',
              boxSizing: 'border-box',
              resize: 'vertical',
              direction: dir,
              textAlign
            }}
          />
        </div>

        <div style={{ 
          display: 'flex', 
          gap: '15px', 
          justifyContent: 'center', 
          marginTop: '30px',
          flexDirection: isRTL ? 'row' : 'row-reverse'
        }}>
          <button
            type="button"
            onClick={() => navigate('/banks')}
            style={{
              backgroundColor: '#666',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 'bold'
            }}
          >
            {t('banks.form.cancel', { defaultValue: 'Cancel' })}
          </button>
          <button
            type="submit"
            disabled={isLoading}
            style={{
              backgroundColor: isLoading ? '#666' : '#AC2000',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: 'bold',
              opacity: isLoading ? 0.7 : 1
            }}
          >
            {isLoading ? t('banks.form.saving', { defaultValue: 'Saving...' }) : t('banks.form.save', { defaultValue: 'Save' })}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditBank;
